// --- Build a curriculum-like tree from a resolved collection ---
// SUBJECT.id  = real board subject_id
// CHAPTER.id  = real board chapter_id
// TOPIC.id    = "col-<collectionId>-t-<idx>" (unchanged)
// --- Build a curriculum-like tree from a resolved collection (Subject → Chapter → Topics)
export function buildCollectionTree(resolved, collectionMeta, t) { // BDD 12/15/2025 - added t here
  if (!resolved?.items?.length) return [];

  const collId = collectionMeta?.id ?? "x";

  // 1) Group items by Subject (prefer real board subject_id + name)
  const subjMap = new Map();
  resolved.items.forEach((it, idx) => {
    const subjectId = it.subject_id ?? it.topic_snapshot?.subject_id ?? null;
    const subjectName = it.subject || it.topic_snapshot?.subject || "—";
    const subjKey = `${subjectId ?? "na"}|${subjectName}`;
    if (!subjMap.has(subjKey)) subjMap.set(subjKey, []);
    subjMap.get(subjKey).push({ it, idx });
  });

  const subjects = Array.from(subjMap.entries()).map(([subjKey, itemsInSubject], sIdx) => {
    const [subjIdStr, subjName] = subjKey.split("|");
    const subjId = subjIdStr === "na" ? undefined : Number(subjIdStr);

    // 2) Within a subject, group by Chapter (prefer real board chapter_id + name)
    const chapMap = new Map();
    itemsInSubject.forEach(({ it, idx }) => {
      const chapterId = it.chapter_id ?? it.topic_snapshot?.chapter_id ?? null;
      const chapterName = it.chapter || it.topic_snapshot?.chapter || "—";
      const chapKey = `${chapterId ?? "na"}|${chapterName}`;
      if (!chapMap.has(chapKey)) chapMap.set(chapKey, []);
      chapMap.get(chapKey).push({ it, idx });
    });

    const chapters = Array.from(chapMap.entries()).map(([chapKey, itemsInChapter], cIdx) => {
      const [chapIdStr, chapName] = chapKey.split("|");
      const chapId = chapIdStr === "na" ? undefined : Number(chapIdStr);

      // 3) Topics under each chapter
      const topics = itemsInChapter.map(({ it, idx }) => {
        // Normalize videos so handleVideoClick(video.source_id, video.tag_id, ...) works
        const videos = ((it.videos ?? it.videos_payload) || [])
          // .map((v, i) => ({
          //   name: v.name || v.segment_name || `Segment ${i + 1}`,
          //   source_id: v.source_id ?? v.video_source_id ?? v.id ?? v.video_id ?? null,
          //   tag_id: v.tag_id ?? v.tag ?? null,
          // }))
          
          // BDD 12/09/2025 Localization For Collections
          .map((v, i) => {
            const localizedLabel =
              v.display_name ||
              v.segment_name ||
              v.name ||
              `Segment ${i + 1}`;
        
            const fullName =
              v.name ||
              v.segment_name ||
              localizedLabel;
        
            return {
              name: fullName,
              display_name: localizedLabel,
              source_id: v.source_id ?? v.video_source_id ?? v.id ?? v.video_id ?? null,
              tag_id: v.tag_id ?? v.tag ?? null,
            };
          })

          .filter(v => v.source_id);  // allow no-tag videos (tag_id=null) for collection 73 Earth System videos

        // Normalize pwos so handlePWOClick(p.pwo_source_id, p.scene_name, ...) works
        // const pwos = ((it.pwos ?? it.pwos_payload) || [])
        //   .map((p, i) => ({
        //     name: p.name || p.scene_title || `Scene ${i + 1}`,
        //     pwo_source_id: p.pwo_source_id ?? p.source_id ?? null,
        //     scene_name: p.scene_name ?? p.scene ?? null,
        //   }))
        //   .filter(p => p.pwo_source_id && p.scene_name);

        // BDD 12/09/2025 Localization For Collections
        const pwos = ((it.pwos ?? it.pwos_payload) || [])
          .map((p, i) => {
            // Prefer any localized short label the backend put in display_name,
            // then fallback to name, then old scene_title, then generic.
            const localizedLabel =
              p.display_name ||
              p.scene_title ||
              p.name ||
              `${t("Scene") ?? "Scene"} ${i + 1}`;

            const fullName =
              p.name ||
              p.scene_title ||
              localizedLabel;

            return {
              // what most callers will show
              name: fullName,
              // what openPwoPicker's labelForPwo prefers
              display_name: localizedLabel,

              pwo_source_id: p.pwo_source_id ?? p.source_id ?? null,
              scene_name: p.scene_name ?? p.scene ?? null,
            };
          })
          .filter(p => p.pwo_source_id && p.scene_name);
        // BDD 12/09/2025 Localization For Collections
        // BDD NOTE CAUTION Probably the right thing to do would be to realy have localization for 
        // title as the teacher might have given a name or while creating allow localization
        const topicName =
          it.topic ||               // localized header from backend
          it.title ||               // fallback to older module title / override
          `Topic ${idx + 1}`;

        return {
          // Keep synthetic topic ids → your toggle/resolve logic continues to work
          id: `col-${collId}-t-${idx}`,
          // name: it.title || it.topic || `Topic ${idx + 1}`,
          // BDD 12/09/2025 Localization For Collections
          name: topicName,
          display_name: topicName,  // optional, makes labelOf() happier
          // So toggleExpandTopic can fetch content by real board_topic_id
          board_topic_id: it.board_topic_id ?? it.topic_snapshot?.board_topic_id ?? null,
          // So fetchQuiz uses canonical source topic id
          source_id: it.source_topic_id ?? it.topic_snapshot?.source_topic_id ?? null,
          topic_image_path: it.topic_image_path ?? it.topic_snapshot?.topic_image_path ?? null, // BDD 10/3/2025 - topicimagefor club collections
          videos,
          pwos,
        };
      });

      return {
        // Prefer the **real board chapter id**; fallback to a stable synthetic if missing
        id: chapId ?? `col-${collId}-chap-${cIdx}`,
        name: chapName,
        topics,
      };
    });

    return {
      // Prefer the **real board subject id**; fallback to a stable synthetic if missing
      id: subjId ?? `col-${collId}-sub-${sIdx}`,
      name: subjName,
      chapters,
    };
  });

  return [{
    id: `col-${collId}-lvl`,
    grade: "", // we’re intentionally omitting level/grade in the collections view
    subjects,
  }];
}